import { format, formatDistanceToNow } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { parseTimestamp, userTimeZone } from '@/utils/datetime';

interface RelativeTimeProps {
  /**
   * The timestamp to render. Accepts RFC 3339 (`...Z` or `...±HH:MM`),
   * Postgres-style `"YYYY-MM-DD HH:MM:SS"` (treated as UTC), or
   * pre-parsed `Date`. `null`/`undefined`/invalid renders as the
   * fallback string.
   */
  value: string | Date | null | undefined;
  /** Rendered when `value` is missing or unparseable. */
  fallback?: string;
  /** Pass-through className for the trigger element. */
  className?: string;
  /**
   * When true, render only the absolute time and skip the relative
   * phrasing — keeps the tooltip behavior for hover even when
   * "3 minutes ago" would be too coarse (e.g., audit-log rows where
   * the operator cares about the actual instant).
   */
  absolute?: boolean;
}

/**
 * Display a timestamp as relative-to-now ("2 minutes ago") with a
 * hover tooltip that shows the same instant in BOTH the user's
 * local time (with timezone abbreviation) AND UTC.
 *
 * Showing both is intentional: operators routinely compare a UI
 * timestamp against a server log line that's in UTC, and the
 * mental "subtract 5 hours" step is exactly where alignment
 * errors creep in. The tooltip removes that step.
 */
export function RelativeTime({
  value,
  fallback = '—',
  className,
  absolute = false,
}: RelativeTimeProps) {
  const date = value instanceof Date ? value : parseTimestamp(value);
  if (!date) {
    return <span className={className}>{fallback}</span>;
  }

  const display = absolute
    ? format(date, 'PPp')
    : formatDistanceToNow(date, { addSuffix: true });

  // Render local and UTC on separate lines. Local has the zone
  // abbreviation suffix so an operator who's currently in a
  // different zone than the one their browser is set to (laptop
  // travelling, ssh into a box from another tz) immediately sees
  // what zone the conversion was against.
  const tz = userTimeZone();
  const localLine = `${format(date, 'PPpp')} (${tz})`;
  const utcLine = `${date
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d+Z$/, '')} UTC`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={className}>{display}</span>
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          <div className="font-medium">{localLine}</div>
          <div className="text-muted-foreground">{utcLine}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
